import { Grid, Typography } from "@mui/material";
import { TRADE_HISTORY_TABS, TRADE_SIDE, TRADE_TYPE, WALLET_TYPE } from "../constants";
import { TradeInsertConfirmedUser, TradeInsertInitial } from "store/api/trade";
import { createSearchParams, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getMarginOpenOrderList, getMarginTradeLast24HList, getSpotOpenOrderList, getSpotTradeLast24HList, getTradeAmountMax } from "store/slices/trade/tradeSlice";
import { LoadingButton } from "@mui/lab";
import NumberFormats from "components/NumberFormats";
import TradeInitialConfirmModal from "../Modals/TradeInitialConfirmModal";
import { checkIfRulesAreAccepted } from "utils/helpers/checkIfRulesAreAccepted";
import http from "config/http";
import { scrollSection } from "utils/helpers/scrollSection";
import useAlertModal from "hooks/useAlertModal";
import useDispatch from "hooks/useDispatch";
import { useFormContext } from "react-hook-form";
import { useMemo } from "react";
import useNotify from "hooks/useNotify";
import { useState } from "react";
import SuccessModal from "components/SuccessModal";
import SuccessTradeInitial from "../Modals/SuccessTradeInitial";

function SubmitTrade({ amountPlaceholderCoin, minimumTradeAmount, maximumTradeAmount }) {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const location = useLocation();
  const dispatch = useDispatch();

  const { source, destination } = useParams();
  const alert = useAlertModal();
  const navigate = useNavigate();

  const { handleSubmit, watch, reset } = useFormContext();
  const sideId = watch("sideId");
  const amount = watch("amount");
  const isPrice = watch("isPrice");
  const price = watch("price");

  const [searchParams] = useSearchParams();
  const allSearchParams = Object.fromEntries([...searchParams]);
  const tradeHistoryTab = +searchParams.get("tradeHistoryTab") || 0;
  const walletTypeId = +searchParams.get("walletTypeId") || 1;
  const pageNumber = searchParams.get("page") || 1;
  const pageSize = searchParams.get("perPage") || 10;

  const [tradeInsertInitial, setTradeInsertInitial] = useState(null);
  const [showTradeInitialConfirmModal, setShowTradeInitialConfirmModal] = useState(false);
  const [showConfirmAcceptModal, setConfirmAcceptModal] = useState(false);

  const onSubmit = async (payload) => {
    const body = { ...payload, price: payload?.price || 0 };

    delete body.isPrice;
    try {
      setLoading(true);

      const { data } = await http.post(TradeInsertInitial, body);

      setTradeInsertInitial(data?.data);
      setShowTradeInitialConfirmModal(true);
    } catch (error) {
      checkIfRulesAreAccepted({ error, notify, alert, navigate });
    }

    setLoading(false);
  };

  const updateTradeHistory = () => {
    if (walletTypeId === WALLET_TYPE.margin) {
      if (tradeHistoryTab === TRADE_HISTORY_TABS.openOrder) {
        dispatch(
          getMarginOpenOrderList({
            coinSourceId: -1,
            coinDestinationId: -1,
            sideId: 0,
            typeId: 0,
            pageNumber,
            pageSize,
          })
        );
      } else {
        dispatch(getMarginTradeLast24HList());
      }
    } else {
      if (tradeHistoryTab === TRADE_HISTORY_TABS.openOrder) {
        dispatch(
          getSpotOpenOrderList({
            coinSourceId: -1,
            coinDestinationId: -1,
            sideId: 0,
            typeId: 0,
            pageNumber,
            pageSize,
          })
        );
      } else {
        dispatch(getSpotTradeLast24HList());
      }
    }
  };

  const handleConfirmTradeInitial = async (payload) => {
    const body = {
      tradeId: payload?.id,
      tradeTypeId: payload.typeId,
    };

    try {
      setLoading(true);
      const { data } = await http.post(TradeInsertConfirmedUser, body);
      updateTradeHistory();
      reset();
      setTradeInsertInitial(data);
      setShowTradeInitialConfirmModal(false);
      setConfirmAcceptModal(true);
    } catch (error) {
      notify(error?.response?.data?.message, "error");
    }
    setLoading(false);
  };

  const handleCloseConfirmAcceptModal = () => {
    const isMarketTrade =
      tradeInsertInitial?.data?.typeId === TRADE_TYPE.market.id;

    navigate({
      pathname: location.pathname,
      search: `?${createSearchParams({
        ...allSearchParams,
        tradeHistoryTab: isMarketTrade
          ? TRADE_HISTORY_TABS.log24
          : TRADE_HISTORY_TABS.openOrder,
      })}`,
    });

    setConfirmAcceptModal(false);

    scrollSection("trade_history");

    dispatch(
      getTradeAmountMax({
        coinId: source,
        coinMasterId: destination,
        sideId,
        walletTypeId: walletTypeId,
      })
    );
  };

  const handleCloseTradeInitialConfirmModal = () => {
    setTradeInsertInitial(null);
    setShowTradeInitialConfirmModal(false);
  };

  const isConfirmDisabled = useMemo(() => {
    if (isPrice) {
      return !amount || !price;
    } else {
      return !amount;
    }
  }, [amount, isPrice, price]);

  const buttonColor = useMemo(() => {
    if (sideId === TRADE_SIDE.Buy.id) return TRADE_SIDE.Buy.color;
    return TRADE_SIDE.Sell.color;
  }, [sideId]);

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" component="p" color="text.secondary">
            Maximum:{" "}
            <Typography
              variant="h6"
              component="b"
              color="text.main"
              fontWeight={600}
            >
              {" "}
              <NumberFormats value={maximumTradeAmount} />{" "}
            </Typography>
            ( {amountPlaceholderCoin} )
          </Typography>

          <Typography variant="subtitle1" component="p" color="text.secondary">
            Minimum:{" "}
            <Typography
              fontWeight={600}
              variant="h6"
              component="b"
              color="text.main"
            >
              {" "}
              <NumberFormats value={minimumTradeAmount} />{" "}
            </Typography>
            ( {amountPlaceholderCoin} )
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={isConfirmDisabled}
            fullWidth
            color={buttonColor}
            onClick={handleSubmit(onSubmit)}
          >
            Confirm{" "}
            {sideId === TRADE_SIDE.Buy.id
              ? TRADE_SIDE.Buy.name
              : TRADE_SIDE.Sell.name}
          </LoadingButton>
        </Grid>
      </Grid>

      <TradeInitialConfirmModal
        tradeInsertInitial={tradeInsertInitial}
        open={showTradeInitialConfirmModal}
        loading={loading}
        onClose={handleCloseTradeInitialConfirmModal}
        handleConfirmTradeInitial={handleConfirmTradeInitial}
      />

      <SuccessModal
        open={showConfirmAcceptModal}
        onClose={handleCloseConfirmAcceptModal}
        title={"Trade Executed Successfully"}
        subtitle={"Check your portfolio for updated balances."}
      >
        <SuccessTradeInitial tradeInsertInitial={tradeInsertInitial} />
      </SuccessModal>
    </>
  );
}

export default SubmitTrade;
